import { Button, Icon, useSnackbar } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { useTranslation } from "react-i18next";
import {
  useGetIcbtFeedbacks,
  useGetIcbtResponses,
} from "../../../../../../api/useIcbtQueries";
import { usePsychologistUser } from "../../../../../../hooks";
import { styled } from "styled-components";

type CopyToClipboardProps = {
  userId: number;
  icbtRequestId: string;
  programName: string;
  patientName: string;
  disabled?: boolean;
};

export const CopyToClipboard = ({
  userId,
  icbtRequestId,
  programName,
  patientName,
  disabled,
}: CopyToClipboardProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const isCopyToClipboardEnabled = useFlag("COPY_ICBT_FEEDBACK_FOR_JOURNAL");
  const { data: feedbacks } = useGetIcbtFeedbacks({
    userId,
    icbtRequestId,
  });

  const { data: patientNotes } = useGetIcbtResponses({
    userId,
    icbtRequestId,
  });
  const { psychologist } = usePsychologistUser({ includeDetails: true });

  const hasPublishedPsychologistFeedback = feedbacks?.some(
    (feedback) => feedback.status === "published",
  );

  const handleCopyToClipboard = async () => {
    try {
      const notes =
        !patientNotes || patientNotes.length === 0
          ? t("NO_NOTES_LEGACY")
          : patientNotes.map((note) => note.response_text).join("\n");

      const text =
        `${programName} ${
          feedbacks?.[0].published_at
            ? new Date(feedbacks?.[0].published_at).toLocaleDateString()
            : "[DATE IS MISSING!]" // this should be a noop
        }` +
        "\n" +
        patientName +
        "\n" +
        notes +
        "\n\n" +
        feedbacks
          ?.map((feedback) => {
            return (
              `${psychologist.user.firstName} ${psychologist.user.lastName} ${t(
                psychologist.details.headlineEnum,
              )}` +
              "\n" +
              feedback.feedback_text
            );
          })
          .join("\n\n");

      await navigator.clipboard.writeText(text);
      enqueueSnackbar({
        text: t("COPIED_ALL_NOTES_AND_FEEDBACK"),
      });
    } catch (error) {
      console.error("Failed to copy to clipboard", error);
    }
  };

  return isCopyToClipboardEnabled && hasPublishedPsychologistFeedback ? (
    <ButtonWithTooltip
      data-title={t("COPY_ALL_NOTES_AND_FEEDBACK")}
      variant="icon"
      onClick={handleCopyToClipboard}
      disabled={disabled}
    >
      <Icon.Copy />
    </ButtonWithTooltip>
  ) : null;
};

const ButtonWithTooltip = styled(Button)`
  position: relative;

  &::before {
    background-color: #554f4d;
    border: 1px solid #888;
    border-radius: 2px;
    color: white;
    content: attr(data-title);
    display: none;
    font-size: 0.75rem;
    padding: 6px 8px;
    position: absolute;
    z-index: 1;
    top: 45px;
    white-space: nowrap; /* Prevent line breaks */
    width: max-content; /* Fit text width */
  }

  &:hover::before {
    display: block;
  }
`;
